import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonVariants, ButtonSizes } from '@carboledger/ui';
import { Plus, FileUp } from '@carboledger/ui/icons';
import { PageHeader } from '@/components/layout';
import { ROUTES } from '@/app/routes';
import { useTranslations, useAssistantFilters } from '@/hooks';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import {
  useGetContractDiscrepancyQuery,
  useDeleteContractMutation,
  useUpdateContractStatusMutation,
} from '../api/contractsApi';
import { linksApi, useLinkShipmentContractsMutation } from '@/lib/api/linksApi';
import {
  LinkShipmentsModal,
  LinkShipmentsForContract,
  LINK_FLOWS,
  buildContractLinkDetailFields,
  aggregateLinkRows,
  useLinkableCandidates,
} from '@/components/common';
import { buildContractLinksPayload } from '@/lib/mappers/links';
import { buildContractDiscrepancySections } from '../constants/contractDiscrepancy';
import { ContractsTable } from '../components/ContractsTable';
import {
  buildContractActions,
  contractRowActions,
  contractMenuActions,
  nextContractStatus,
} from '../constants/contractActions';
import { ConfirmDialog } from '@/components/feedback';
import { AddAttachmentModal, DiscrepancyModal } from '@/components/common';
import { ATTACHMENT_TARGETS } from '@/lib/api/attachmentsApi';
import { EMPTY_LIST } from '@/lib/utils';
import styles from './ContractsPage.module.scss';

// Identity rows the bulk card only shows when every selected contract agrees on them.
const CONTRACT_IDENTITY_KEYS = [
  'materialName',
  'supplierName',
  'facilityName',
  'terminal',
  'deliverBy',
];
// Inbounds ▸ Contracts.
export const ContractsPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module } = useParams();
  const assistantFilters = useAssistantFilters();

  const [discrepancyRow, setDiscrepancyRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteContract] = useDeleteContractMutation();
  const [updateContractStatus] = useUpdateContractStatusMutation();

  const [linkRow, setLinkRow] = useState(null);
  const [linkContracts, setLinkContracts] = useState(EMPTY_LIST);
  const [linkSearch, setLinkSearch] = useState('');
  const tableRef = useRef(null);
  const [linkShipmentContracts, { isLoading: isLinkingContracts }] =
    useLinkShipmentContractsMutation();

  const handleOpenDiscrepancy = useCallback((row) => setDiscrepancyRow(row), []);
  const handleCloseDiscrepancy = useCallback(() => setDiscrepancyRow(null), []);

  const handleViewContract = useCallback(
    (row) => {
      navigate(ROUTES.contractDetail(module, row.id));
    },
    [navigate, module],
  );

  const handleAddContract = useCallback(() => {
    navigate(ROUTES.contractCreate(module));
  }, [navigate, module]);

  const handleReceiveShipment = useCallback(
    (row) => {
      navigate(ROUTES.contractReceiveShipment(module, row.id));
    },
    [navigate, module],
  );

  const handleLinkShipment = useCallback((row) => {
    setLinkContracts(EMPTY_LIST);
    setLinkRow(row);
  }, []);

  // <Table> hands the bulk action its own selection, so the page keeps no copy.
  const handleBulkLinkShipment = useCallback((rows) => {
    setLinkRow(null);
    setLinkContracts(rows);
  }, []);

  const handleCloseLink = useCallback(() => {
    setLinkRow(null);
    setLinkContracts(EMPTY_LIST);
    setLinkSearch('');
  }, []);

  const handleLinkSearchChange = useCallback((term) => setLinkSearch(term), []);

  const handleToggleClosed = useCallback(
    (row) => {
      updateContractStatus({ module, id: row.id, status: nextContractStatus(row) });
    },
    [updateContractStatus, module],
  );

  const handleEditContract = useCallback(
    (row) => {
      navigate(ROUTES.contractEdit(module, row.id));
    },
    [navigate, module],
  );

  const [attachmentRow, setAttachmentRow] = useState(null);
  const handleAddAttachment = useCallback((row) => setAttachmentRow(row), []);
  const handleCloseAttachment = useCallback(() => setAttachmentRow(null), []);

  const handleDeleteContract = useCallback((row) => setDeleteRow(row), []);
  const handleCancelDelete = useCallback(() => setDeleteRow(null), []);
  const handleConfirmDelete = useCallback(async () => {
    await deleteContract({ module, id: deleteRow.id });
    setDeleteRow(null);
  }, [deleteContract, module, deleteRow]);

  const isBulkLink = linkContracts.length > 0;

  const bulkQueryArgs = useMemo(
    () => linkContracts.map((row) => ({ contractId: row.id, search: linkSearch })),
    [linkContracts, linkSearch],
  );

  const { candidates: bulkCandidates, loading: isLoadingBulkCandidates } = useLinkableCandidates(
    linksApi.endpoints.getLinkableShipments,
    bulkQueryArgs,
  );

  const bulkTitle = t('LINK_SHIPMENTS_TITLE', {
    contractId: linkContracts.map((row) => row.contractId).join(', '),
  });

  // The selection reads as one contract, so the bulk card is the row card's own builder.
  const bulkContract = useMemo(
    () => aggregateLinkRows(linkContracts, CONTRACT_IDENTITY_KEYS),
    [linkContracts],
  );

  const bulkDetailFields = useCallback(
    (unitType, assignedInUnit) =>
      buildContractLinkDetailFields(t, bulkContract, undefined, unitType, assignedInUnit),
    [t, bulkContract],
  );

  const rowDetailFields = useCallback(
    (unitType, assignedInUnit) =>
      buildContractLinkDetailFields(t, linkRow, undefined, unitType, assignedInUnit),
    [linkRow, t],
  );

  const handleSaveBulkLinks = useCallback(
    async (entries, pickedShipmentId) => {
      const result = await linkShipmentContracts({
        module,
        shipmentId: pickedShipmentId,
        contractIds: linkContracts.map((row) => row.id),
        links: buildContractLinksPayload(entries),
      });
      if (result.error) return;
      tableRef.current?.clearSelection();
      handleCloseLink();
    },
    [linkShipmentContracts, module, linkContracts, handleCloseLink],
  );

  const bulkActions = useMemo(
    () => [
      {
        id: 'link-shipment',
        label: t('ACTION_LINK_SHIPMENT'),
        onClick: handleBulkLinkShipment,
      },
    ],
    [t, handleBulkLinkShipment],
  );

  // No View action — the Contract ID cell is the row's link to its detail page.
  const contractActions = useMemo(
    () =>
      buildContractActions(t, {
        onReceiveShipment: handleReceiveShipment,
        onLinkShipment: handleLinkShipment,
        onAddAttachment: handleAddAttachment,
        onToggleClosed: handleToggleClosed,
        onEdit: handleEditContract,
        onDelete: handleDeleteContract,
      }),
    [
      t,
      handleReceiveShipment,
      handleLinkShipment,
      handleAddAttachment,
      handleToggleClosed,
      handleEditContract,
      handleDeleteContract,
    ],
  );

  const rowActions = useMemo(() => contractRowActions(contractActions), [contractActions]);
  const rowMenuActions = useMemo(() => contractMenuActions(contractActions), [contractActions]);

  const addIcon = useMemo(() => <Plus />, []);
  const importIcon = useMemo(() => <FileUp />, []);

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('CONTRACTS_EMPTY_TITLE'),
      description: t('CONTRACTS_EMPTY_DESCRIPTION'),
      primaryLabel: t('CONTRACTS_ADD'),
      onPrimaryClick: handleAddContract,
      primaryIcon: addIcon,
    }),
    [t, handleAddContract, addIcon],
  );

  const noResults = useMemo(
    () => ({
      title: t('CONTRACTS_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  const headerActions = useMemo(
    () => (
      <>
        <Button
          variant={ButtonVariants.secondary}
          size={ButtonSizes.sm}
          leftIcon={importIcon}
          // TODO: enable once the shared .xlsx import wizard exists.
          disabled
        >
          {t('CONTRACTS_IMPORT')}
        </Button>
        <Button
          variant={ButtonVariants.primary}
          size={ButtonSizes.sm}
          leftIcon={addIcon}
          onClick={handleAddContract}
        >
          {t('CONTRACTS_ADD')}
        </Button>
      </>
    ),
    [t, handleAddContract, importIcon, addIcon],
  );

  // currentData, not data: `data` keeps the previous contract while a new one loads.
  const { currentData: discrepancyContract, isFetching: isDiscrepancyLoading } =
    useGetContractDiscrepancyQuery(discrepancyRow?.id, { skip: !discrepancyRow });

  const discrepancySections = useMemo(
    () => buildContractDiscrepancySections(t, discrepancyContract),
    [t, discrepancyContract],
  );

  return (
    <div className={styles.page}>
      <PageHeader title={t('CONTRACTS_TITLE')} actions={headerActions} />
      <ContractsTable
        tableRef={tableRef}
        extraFilters={assistantFilters}
        emptyState={emptyState}
        noResults={noResults}
        onOpenContract={handleViewContract}
        onOpenDiscrepancy={handleOpenDiscrepancy}
        rowActions={rowActions}
        rowMenuActions={rowMenuActions}
        bulkActions={bulkActions}
      />

      <DiscrepancyModal
        open={discrepancyRow != null}
        onClose={handleCloseDiscrepancy}
        referenceId={discrepancyRow?.contractId}
        discrepancies={discrepancyContract?.discrepancies}
        sections={discrepancySections}
        loading={isDiscrepancyLoading}
      />

      <LinkShipmentsForContract
        module={module}
        contractDbId={linkRow?.id}
        contractLabel={linkRow?.contractId}
        detailFields={rowDetailFields}
        open={linkRow != null}
        onClose={handleCloseLink}
      />

      {/* Closing unmounts it, so nothing it held survives into the next open. */}
      {isBulkLink ? (
        <LinkShipmentsModal
          open
          onClose={handleCloseLink}
          title={bulkTitle}
          detailFields={bulkDetailFields}
          shipments={bulkCandidates}
          loading={isLoadingBulkCandidates}
          flow={LINK_FLOWS.contractsToShipment}
          bulkRows={linkContracts}
          isSaving={isLinkingContracts}
          onSearchChange={handleLinkSearchChange}
          onSave={handleSaveBulkLinks}
        />
      ) : null}

      <AddAttachmentModal
        open={attachmentRow != null}
        onClose={handleCloseAttachment}
        title={t('CONTRACTS_ADD_ATTACHMENT_MODAL_TITLE', { contractId: attachmentRow?.contractId })}
        target={ATTACHMENT_TARGETS.contract}
        module={module}
        entityId={attachmentRow?.id}
      />

      <ConfirmDialog
        open={deleteRow != null}
        title={t('CONTRACTS_DELETE_TITLE')}
        bodyTitle={t('CONTRACTS_DELETE_BODY_TITLE', { contractId: deleteRow?.contractId })}
        description={t('CONTRACTS_DELETE_DESCRIPTION')}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};
